import React, { useCallback, useMemo } from 'react';
import { Button, Grid, Theme, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { LinkComponentProps } from 'components/common/LinkComponent';
import { formatPriceStart } from 'utils/format';
import IconCalendarWhite from '../svg/IconCalendarWhite.svg';
import IconClockWhite from '../svg/IcoClockWhite.svg';
import EventPassportRequiredInfoContainer from '../eventV2/EventPassportRequiredInfoContainer';
import { trackButtonClick } from '../screens/ScrenTracker';

export interface EventCallToActionProps extends LinkComponentProps {
  eventId: number;
  hasTickets?: boolean;
  minPrice?: number;
  maxPrice?: number;
  currency?: string;
  ticketsSaleStatus?: string;
  ctaButtonLabelDisplay: string;
  position?: string;
  borderRadius?: string;
  height?: number;
  showTime?: number;
  duration?: string;
  time?: any;
  dontShowActionButton?: boolean;
  info?: React.ReactNode;
  hideDate: boolean;
  hideTime: boolean;
  isHiddenPrice?: boolean;
}

interface StyleProps {
  position?: string;
  borderRadius?: string;
  height?: number;
}

const BUILD_ENV = process.env.NEXT_PUBLIC_BUILD_ENV;
const isTestEnv = () => BUILD_ENV === 'test';
const isProductionEnv = () => BUILD_ENV === 'production';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  containerWrapper: {
    ...(props.height && { height: props.height }),
    minHeight: 80,
    position: props.position,
    borderRadius: props.borderRadius,
    bottom: 0,
    width: '100%',
    padding: theme.spacing(2, 4, 2, 4.25),
    zIndex: 1,
    color: theme.palette.custom.white,
    backgroundColor: theme.palette.primary.main,
    '@media (max-width: 390px)': {
      paddingLeft: '16px',
      paddingRight: '16px',
    },
  },
  colorDisabled: {
    backgroundColor: theme.palette.custom.gray.regular,
  },
  textContainer: {
    textAlign: 'left',
    marginRight: theme.spacing(2),
    wordBreak: 'break-word',
    [theme.breakpoints.down('md')]: {
      paddingLeft: '24px !important',
      textAlign: 'left',
    },
    '@media (min-width: 576px)': {
      width: '100%',
    },
    '@media (max-width: 390px)': {
      paddingLeft: '0px !important',
      marginRight: 0,
    },
  },
  textContainerWrapper: {
    '@media (min-width: 576px)': {
      width: '100%',
      justifyContent: 'space-between',
    },
  },
  button: {
    borderRadius: 100,
    minWidth: 144,
    '&.disabled': {
      background: 'rgba(255, 255, 255, 0.5)',
    },
  },
}));

const EventCallToAction: React.FC<React.PropsWithChildren<EventCallToActionProps>> = (props) => {
  const {
    eventId,
    hasTickets,
    minPrice,
    maxPrice,
    currency,
    ticketsSaleStatus,
    ctaButtonLabelDisplay,
    height,
    position = 'sticky',
    borderRadius = '0 0 0 0',
    time,
    duration,
    dontShowActionButton,
    info,
    hideDate,
    hideTime,
    isHiddenPrice,
    ...rest
  } = props;
  const { classes, cx } = useStyles({ position, borderRadius, height });
  const disabled = !hasTickets || ticketsSaleStatus !== 'on_sale';
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'));
  const less1090 = useMediaQuery('(max-width: 1090px)');

  const getPrice = () => {
    if (isHiddenPrice) return '';
    if (eventId === 6047 && isProductionEnv() && !minPrice && !maxPrice) return '';
    if (eventId === 1953 && isTestEnv() && !minPrice && !maxPrice) return '';
    return formatPriceStart(minPrice || 0, maxPrice || 0, currency, true, true);
  };

  const justifyContent = useMemo(() => {
    if (info) return 'end';
    if (duration && dontShowActionButton) return 'space-between';
    return 'center';
  }, [dontShowActionButton, duration, info]);

  const handleOnClick = useCallback(() => {
    trackButtonClick('Registration Button Clicked', ctaButtonLabelDisplay, {
      button: 'registration_button',
      button_text: ctaButtonLabelDisplay,
      event: eventId,
    });
  }, [ctaButtonLabelDisplay, eventId]);

  return (
    <>
      {isMobile && info && <EventPassportRequiredInfoContainer mt={-50} mlr={20} />}
      <Grid
        container
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        className={cx(classes.containerWrapper, { [classes.colorDisabled]: disabled })}
        alignItems="center"
        justifyContent={justifyContent}
      >
        {duration && (!info || !less1090) && (
          <Grid style={{ paddingLeft: 40 }} item xs>
            <Grid container spacing={3}>
              {!hideDate && (
                <Grid item>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid style={{ paddingTop: 12 }} item>
                      <IconCalendarWhite />
                    </Grid>
                    <Grid item>
                      <Typography variant="body1" style={{ fontWeight: 600 }}>
                        {duration}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {!hideTime && time && (
                <Grid item>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid style={{ paddingTop: 12 }} item>
                      <IconClockWhite fill="white" stroke="white" />
                    </Grid>
                    <Grid item>
                      <Typography variant="body1" style={{ fontWeight: 600 }}>
                        {time}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
        <Grid
          style={{ ...(duration && { paddingRight: 40 }) }}
          item
          {...(!dontShowActionButton && { xs: !duration || 0 })}
        >
          <Grid container alignItems="center" spacing={duration ? 5 : 0}>
            {!isMobile && info && <Grid item>{info}</Grid>}
            <Grid
              item
              xs
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              className={classes.textContainer}
            >
              <Typography color="inherit" variant="subtitle2">
                {getPrice()}
              </Typography>
            </Grid>

            {!dontShowActionButton && (
              <Grid item>
                <Button
                  {...rest}
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  className={cx(classes.button, { disabled })}
                  id="get-tickets-button"
                  variant="contained"
                  color="secondary"
                  disabled={disabled}
                  onClick={handleOnClick}
                >
                  {ctaButtonLabelDisplay}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default EventCallToAction;
